import { useEffect, useState } from "react";
import type { DisplayGame, StandingsByConference } from "../types/nba";
import type { EventCard } from "../ufc/types";

/**
 * Offline (DSPRT-294), the app's side of it. While the site is reachable the
 * last scoreboard and standings are kept on the device; public/offline.html
 * draws them when a later page load finds no connection (public/sw.js serves
 * it then). Nothing here caches the app: see the note at the top of sw.js.
 *
 * Small on purpose: names and numbers only, a few kilobytes, overwritten in
 * place. Profiles and box scores are not saved.
 */
const KEY = "statsummit:offline";

interface Saved {
  scores?: {
    savedAt: string;
    /** The league's day the games are filed under, yyyy-MM-dd. */
    day: string;
    games: Array<{
      id: string; state: string; tipoff: string | null; label: string | null;
      away: { abbr: string; name: string; score: number | null };
      home: { abbr: string; name: string; score: number | null };
    }>;
  };
  standings?: {
    savedAt: string;
    season: string | null;
    conferences: Record<string, Array<{ abbr: string; name: string; wins: number; losses: number; winPct: string }>>;
  };
  /** The UFC card last looked at (DSPRT-302, #85): its fights, with results where there are any. */
  ufc?: {
    savedAt: string;
    name: string;
    startsUtc: string;
    place: string | null;
    state: string;
    bouts: Array<{
      weightClass: string | null; title: boolean; state: string; a: string; b: string;
      /** "a", "b", or null for a fight not decided (or drawn). */
      winner: "a" | "b" | null;
      /** "Submission · Round 5 · 2:46", or null before the fight. */
      result: string | null;
    }>;
  };
}

function update(change: (s: Saved) => void): void {
  try {
    const s: Saved = JSON.parse(localStorage.getItem(KEY) ?? "{}") ?? {};
    change(s);
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage blocked or full: there is simply nothing saved */
  }
}

/** The scoreboard the visitor was last shown by default (not a day they browsed back to). */
export function saveOfflineScores(day: string, games: DisplayGame[]): void {
  if (games.length === 0) return;
  update((s) => {
    s.scores = {
      savedAt: new Date().toISOString(),
      day,
      games: games.map((g) => ({
        id: g.id, state: g.state, tipoff: g.tipoff ?? null,
        label: g.seasonLabel ?? (g.seasonType === "preseason" ? "Preseason" : null),
        away: { abbr: g.away.abbr, name: g.away.name, score: g.away.score ?? null },
        home: { abbr: g.home.abbr, name: g.home.name, score: g.home.score ?? null },
      })),
    };
  });
}

export function saveOfflineStandings(season: string | null, table: StandingsByConference): void {
  if (table.East.length + table.West.length === 0) return;
  const rows = (c: "East" | "West") => table[c].map((r) => ({ abbr: r.abbr, name: r.name, wins: r.wins, losses: r.losses, winPct: r.winPct }));
  update((s) => {
    s.standings = { savedAt: new Date().toISOString(), season, conferences: { East: rows("East"), West: rows("West") } };
  });
}

/** The card last written, so a card on screen is saved when it changes rather than on every render. */
let savedUfcKey = "";

/** The UFC card on screen: the home page's first card, or the one opened. Safe to call while rendering. */
export function saveOfflineUfc(card: EventCard): void {
  const key = `${card.event.slug}:${card.event.state}:${card.bouts.filter((b) => b.state === "FINAL").length}`;
  if (key === savedUfcKey) return;
  savedUfcKey = key;
  const clock = (s: number | null) => (s == null ? null : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
  const e = card.event;
  update((s) => {
    s.ufc = {
      savedAt: new Date().toISOString(),
      name: e.name,
      startsUtc: e.startsUtc,
      place: [e.venue, e.city].filter(Boolean).join(" · ") || null,
      state: e.state,
      bouts: card.bouts.map((b) => ({
        weightClass: b.weightClass, title: b.titleFight, state: b.state, a: b.a.name, b: b.b.name,
        winner: b.a.winner ? "a" as const : b.b.winner ? "b" as const : null,
        result: b.state === "FINAL"
          ? [b.outcome === "DRAW" ? "Draw" : b.outcome === "NC" ? "No contest" : null, b.methodText ?? b.method,
             b.round ? `Round ${b.round}` : null, clock(b.clockSeconds)].filter(Boolean).join(" · ") || null
          : null,
      })),
    };
  });
}

/**
 * Registers the service worker for every visitor, once the page has loaded
 * (it used to be registered only when alerts were turned on). Production
 * only: on the dev server it would sit between the page and Vite.
 */
export function registerOfflineWorker(): void {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
  const register = () => { navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => undefined); };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}

/**
 * Whether the connection dropped during this visit, and when. The browser's
 * own word for it (navigator.onLine): it is right about "no network at all",
 * which is the case this is for, and says nothing about a slow one.
 * Coming back reloads the page, so everything on it is current again, unless
 * something is being typed, which a reload would throw away.
 */
export function useOffline(): { offline: boolean; since: Date | null } {
  const [since, setSince] = useState<Date | null>(() => (typeof navigator !== "undefined" && navigator.onLine === false ? new Date() : null));
  useEffect(() => {
    const down = () => setSince((s) => s ?? new Date());
    const up = () => {
      const typing = document.activeElement instanceof HTMLElement
        && document.activeElement.matches("input, textarea, select, [contenteditable='true']");
      if (typing) setSince(null);
      else window.location.reload();
    };
    window.addEventListener("offline", down);
    window.addEventListener("online", up);
    return () => {
      window.removeEventListener("offline", down);
      window.removeEventListener("online", up);
    };
  }, []);
  return { offline: since !== null, since };
}
